import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customAxiosInstanceMutator } from './custom-axios-instance';

/**
 * Settings > Notifications, through the notification service: whether this environment sends to
 * customers (LIVE) or redirects everything to test recipients (TEST), where test messages go, and
 * which emails and SMSs are sent at all.
 *
 * Each environment has its own settings: switching dev to LIVE does not affect production.
 */

export type NotificationMode = 'TEST' | 'LIVE';

export interface NotificationTemplateSwitch {
  key: string;
  name: string;
  /** EMAIL or SMS */
  type: string;
  active: boolean;
  description?: string;
}

export interface NotificationSettings {
  mode: NotificationMode;
  testEmailRecipients: string[];
  testSmsNumber: string;
  birthdayTestEmailRecipients: string[];
  templates: NotificationTemplateSwitch[];
  modifiedBy?: string;
  modifiedDate?: string;
}

/** Anything left undefined is left as it is. Template switches are by key. */
export interface NotificationSettingsUpdate {
  mode?: NotificationMode;
  testEmailRecipients?: string[];
  testSmsNumber?: string;
  birthdayTestEmailRecipients?: string[];
  templates?: Record<string, boolean>;
  changedBy?: string;
}

const SETTINGS_URL = '/notification/api/v1/settings';
export const NOTIFICATION_SETTINGS_QUERY_KEY = [SETTINGS_URL];

export const getNotificationSettings = (signal?: AbortSignal) =>
  customAxiosInstanceMutator<NotificationSettings>({ url: SETTINGS_URL, method: 'GET', signal });

export const updateNotificationSettings = (update: NotificationSettingsUpdate) =>
  customAxiosInstanceMutator<NotificationSettings>({
    url: SETTINGS_URL,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: update
  });

export const useNotificationSettings = (enabled = true) =>
  useQuery({
    queryKey: NOTIFICATION_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => getNotificationSettings(signal),
    enabled
  });

export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: (saved) => {
      queryClient.setQueryData(NOTIFICATION_SETTINGS_QUERY_KEY, saved);
    }
  });
};
